import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTranslation } from "@/hooks/useTranslation";
import { SubscriptionType } from "@shaarp/common";
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from "react-icons/md";

interface SubscriptionTypeSelectorProps {
  subscriptionType: SubscriptionType;
  onSubscriptionTypeChange: (subscriptionType: SubscriptionType) => void;
  monthlyDiscount?: number;
  annualDiscount?: number;
  /** Frequency that is locked (admin-predefined) and cannot be selected. */
  disabledType?: SubscriptionType;
}

export const SubscriptionTypeSelector = ({
  subscriptionType,
  onSubscriptionTypeChange,
  monthlyDiscount,
  annualDiscount,
  disabledType,
}: SubscriptionTypeSelectorProps) => {
  const { t } = useTranslation();

  const monthlyDisabled = disabledType === "monthly";
  const annualDisabled = disabledType === "annual";

  return (
    <div className="mt-4">
      <RadioGroup
        defaultValue="monthly"
        value={subscriptionType}
        onValueChange={(value) => {
          if (value === disabledType) {
            return;
          }
          onSubscriptionTypeChange(value as SubscriptionType);
        }}
        className="flex gap-4"
      >
        <Card
          className={`
            w-full transition relative
            ${
              monthlyDisabled
                ? "cursor-not-allowed opacity-50 border-muted bg-muted/30"
                : "cursor-pointer"
            }
            ${
              subscriptionType === "monthly"
                ? "border-primary border-2 bg-primary/5"
                : !monthlyDisabled
                  ? "border-muted bg-card hover:border-violet/60"
                  : ""
            }
          `}
        >
          <Label
            htmlFor="monthly"
            className={`flex items-center w-full h-full gap-3 px-6 py-4 ${
              monthlyDisabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <RadioGroupItem
              value="monthly"
              id="monthly"
              className="sr-only"
              disabled={monthlyDisabled}
            />
            <span className="flex items-center gap-2">
              {subscriptionType === "monthly" ? (
                <MdRadioButtonChecked />
              ) : (
                <MdRadioButtonUnchecked />
              )}
              <span
                className={`font-medium text-base ${
                  subscriptionType === "monthly"
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                {t("setup.paymentMethod.plan.options.monthly")}
              </span>
              {monthlyDiscount && (
                <Badge
                  style={{ fontSize: 10 }}
                  className="absolute top-[-8px] right-[-8px] text-xs"
                >
                  -{monthlyDiscount}%
                </Badge>
              )}
            </span>
          </Label>
        </Card>
        <Card
          className={`
            w-full transition relative
            ${
              annualDisabled
                ? "cursor-not-allowed opacity-50 border-muted bg-muted/30"
                : "cursor-pointer"
            }
            ${
              subscriptionType === "annual"
                ? "border-primary border-2 bg-primary/5"
                : !annualDisabled
                  ? "border-muted bg-card hover:border-primary/60"
                  : ""
            }
          `}
        >
          <Label
            htmlFor="annual"
            className={`flex items-center w-full h-full gap-3 px-6 py-4 ${
              annualDisabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <RadioGroupItem
              value="annual"
              id="annual"
              className="sr-only"
              disabled={annualDisabled}
            />
            <span className="flex items-center gap-2">
              {subscriptionType === "annual" ? (
                <MdRadioButtonChecked />
              ) : (
                <MdRadioButtonUnchecked />
              )}
              <span
                className={`font-medium text-base ${
                  subscriptionType === "annual"
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                {t("setup.paymentMethod.plan.options.annual")}
              </span>
              <Badge
                style={{ fontSize: 10 }}
                className="absolute top-[-8px] right-[-8px] text-xs"
              >
                {annualDiscount
                  ? `-${annualDiscount}%`
                  : t("setup.paymentMethod.plan.options.discount")}
              </Badge>
            </span>
          </Label>
        </Card>
      </RadioGroup>
    </div>
  );
};
